// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@vanmeter/Overrides/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated, xIsGuest } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import { xCanSeePrices } from "@vanmeter/App/Store/Selectors/ProductSelectors";
import { LoadingDots, parseBoolean } from "@vanmeter/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
    // XNG-Changes
    isProductDetailsPage?: boolean;
    showPricingUom?: boolean;
    showQuoteTooltip?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const isPDP = getCurrentPage(state).type === "ProductDetailsPage";
    const currentLocation = getLocation(state);
    const returnUrl = encodeURIComponent(`${currentLocation.pathname}${currentLocation.search}`);
    const signInPageUrl = (getPageLinkByPageType(state, "SignInPage")?.url || "") + `?returnUrl=${returnUrl}`;
    const signUpPageUrl = (getPageLinkByPageType(state, "CreateAccountPage")?.url || "") + `?returnUrl=${returnUrl}`;

    return {
        currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
        // XNG-Changes
        canSeePrices: xCanSeePrices(state),
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        showPricing: state.context.showPricing,
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        // XNG-Changes
        signInPageUrl,
        signUpPageUrl,
        isAuthenticated: xIsAuthenticated(state),
        isGuest: xIsGuest(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    // XNG-Changes
    priceContainer?: InjectableCss;
    pricePerPricingUomStyle?: TypographyPresentationProps;
    pricePerPricingUomPDP?: TypographyPresentationProps;
    pricePerPricingUomWrapper?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    // XNG-Changes
    lablePriceWrapper?: InjectableCss;
    saleLabelStyle?: TypographyPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    price: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },
    quoteMessage: {
        text: { weight: "bold" },
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    label,
    showUnitOfMeasure = true,
    // XNG-Changes: pack description, will be shown same as partNumbers
    showPack = false,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    showSalePriceLabel = true,
    signInPageUrl,
    signUpPageUrl,
    // XNG-Changes
    isProductDetailsPage = false,
    isAuthenticated,
    showPricingUom = true,
    showQuoteTooltip = true,
    isGuest,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    // XNG-Changes: VM - Classic (Start)
    const pricePerPricingUom =
        pricing?.additionalResults?.pricePerPricingUom ||
        (productContextModel.product
            ? productContextModel.product.properties.pricePerPricingUom
            : cartLineModel.properties.pricePerPricingUom);
    const showPricePerPricingUom = canSeePrices && !quoteRequired && pricePerPricingUom && showPricingUom;

    const showRequireQuote = !quoteRequired && pricing?.unitNetPrice === 0 && pricing?.unitRegularPrice === 0;
    const canSeeListPrice =
        "product" in pricingData
            ? parseBoolean(pricingData.product.properties.canSeeListPrice)
            : parseBoolean(pricingData.properties.canSeeListPrice);
    const guestViewOnly =
        "product" in pricingData
            ? parseBoolean(pricingData.product.properties.guestViewOnly)
            : parseBoolean(pricingData.properties.guestViewOnly);
    // XNG-Changes: VM - Classic (End)

    // XNG-Change: VM-Classic
    if (isGuest && guestViewOnly) {
        return (
            <Typography {...priceStyles.priceText} data-test-selector="productPrice_PricingMessage">
                {siteMessage("Call_For_More_Informations")}
            </Typography>
        );
    }

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("Pricing_SignInForPrice", signInPageUrl || "", signUpPageUrl || "")}
                </Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    // XNG-Change: VM-Classic
    if (quoteRequired || showRequireQuote) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    {showQuoteTooltip && (
                        <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    )}
                    {/* <UnitOfMeasure
                            unitOfMeasure={unitOfMeasure}
                            showUnitOfMeasure={showUnitOfMeasure}
                            styles={styles.unitOfMeasure}
                        /> */}
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel ||
          cartLineModel?.salePriceLabel ||
          // XNG-Changes: VM - Classic
          translate(isAuthenticated ? "Your Price:" : "Web Price:")
        : "";

    const [amount, unit] = pricePerPricingUom?.split(" / ") || [];
    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{label || translate("Price/Unit")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} data-test-selector="priceIncludeVat">
                <StyledWrapper {...styles.priceContainer} className="price-container">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={displayVatForPrice}
                        styles={priceStyles}
                        salePriceLabel={salePriceLabel}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.pricePerPricingUomWrapper}>
                    {isInvalidPrice && showInvalidPriceMessage && (
                        <Typography as="p" {...styles.invalidPriceText}>
                            {translate("Invalid Price")}
                        </Typography>
                    )}
                    {/* XNG-Changes: VM-Classic */}
                    {!isInvalidPrice && showPricePerPricingUom && (
                        <>
                            {isProductDetailsPage ? (
                                <Typography {...styles.pricePerPricingUomPDP}>
                                    {translate("Ext. Price:")} <b>{amount}</b> / {unit}
                                </Typography>
                            ) : (
                                <Typography {...styles.pricePerPricingUomStyle}>({pricePerPricingUom})</Typography>
                            )}
                        </>
                    )}
                    {/* XNG-Changes: VM - Classic */}
                    {pricing && showSavings && canSeeListPrice && (
                        <ProductPriceSavingsMessage
                            pricing={pricing}
                            qtyOrdered={qtyOrdered || 1}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            currencySymbol={currencySymbol}
                            {...styles.savingsMessageText}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        // Xng-Changes
                        showUnitOfMeasure={!isInvalidPrice && showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
}: PriceProps) => {
    // XNG-Changes: VM - Classic
    // let salePriceLabelPrefix = "";
    // if (pricing && salePriceLabel) {
    //     const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
    //     const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
    //     if (unitNetPrice < unitListPrice) {
    //     salePriceLabelPrefix = `${salePriceLabel} `;
    //     }
    // }
    return (
        <>
            {pricing ? (
                <StyledWrapper {...styles.lablePriceWrapper}>
                    <Typography {...styles.saleLabelStyle}>{salePriceLabel}</Typography>
                    <Typography
                        {...styles.priceText}
                        data-test-selector="productPrice_unitNetPrice"
                        className="price-text"
                    >
                        {enableVat && displayWithVat
                            ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                            : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                    </Typography>
                </StyledWrapper>
            ) : (
                // XNG-Changes: show loading dots while price is being fetched
                <LoadingDots />
            )}
            <UnitOfMeasure
                unitOfMeasure={unitOfMeasure}
                showUnitOfMeasure={showUnitOfMeasure}
                styles={styles.unitOfMeasure}
            />
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography
            {...styles?.unitOfMeasureText}
            data-test-selector="productPrice_unitOfMeasureLabel"
            className="uom-text"
        >
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
